import { Alert, Drawer, Empty, Pagination, Spin, Tag } from 'antd';
import { useEffect, useState } from 'react';

import {
  IntegrationLogItem,
  useLazyGetDependencyLogsQuery,
} from '../../services/integrationHealthApi';

const PAGE_SIZE = 20;

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function LogRow(props: { log: IntegrationLogItem }) {
  const { log } = props;
  // A classificação vem do backend (is_error): o log tem status HTTP e também
  // rótulos de texto ('rejected', 'timeout', 'ok') porque nem todo protocolo é
  // HTTP — o RM (SOAP) recusa em 200. Um só lugar decide o que é falha.
  const error = log.is_error;
  // O título é o que a chamada faz; o nome da função desce para a linha
  // técnica. Sem rótulo mapeado o nome da função volta a ser o título — e aí
  // NÃO se repete embaixo, senão a linha mostra o mesmo texto duas vezes.
  const label = log.action_label;
  const title = label || log.action;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '10px 0',
        borderBottom: '1px solid var(--neutral_lightest)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ fontWeight: 600, color: 'var(--neutral_dark)' }}>
          {title}
        </span>
        <Tag color={error ? 'error' : 'success'}>{log.status || '—'}</Tag>
      </div>
      <div style={{ fontSize: 12, color: 'var(--neutral_medium)' }}>
        {formatDateTime(log.created_at)}
        {log.method ? ` · ${log.method}` : ''}
        {label && (
          // Monoespaçada porque é identificador de código, não prosa: é o que
          // o suporte copia para o ticket e procura no repositório.
          <>
            {' · '}
            <span style={{ fontFamily: 'monospace' }}>{log.action}</span>
          </>
        )}
        {log.application ? ` · ${log.application}` : ''}
      </div>
      {log.endpoint && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--neutral_medium)',
            wordBreak: 'break-all',
          }}
        >
          {log.endpoint}
        </div>
      )}
      {log.description && (
        // O quadro vale para os dois desfechos, e não só para a falha. O
        // identificador do documento (a DUV, a escala, a atracação) saiu do
        // título e vem aqui: no título ele era lido como parte do nome da ação
        // e disputava os 200 caracteres da coluna com ela. Verde diz O QUE deu
        // certo; vermelho diz o que falhou e por quê.
        <div
          style={{
            fontSize: 12,
            color: error ? 'var(--error-dark)' : 'var(--success-dark)',
            background: error
              ? 'var(--error-lightest)'
              : 'var(--success-lightest)',
            borderRadius: 6,
            padding: '6px 8px',
            wordBreak: 'break-word',
          }}
        >
          {log.description}
        </div>
      )}
    </div>
  );
}

export function LogsDrawer(props: {
  serviceKey: string | null;
  title: string;
  onClose: () => void;
}) {
  const { serviceKey, title, onClose } = props;
  const [page, setPage] = useState(1);
  // `currentData`, não `data`: o `data` do RTK Query é retentivo. Abrindo o card
  // do PSP e depois o do SERPRO, se a segunda consulta falhasse (403, 500,
  // rede) o drawer titulado "· SERPRO" listava as requisições do PSP — dado
  // atribuído ao serviço errado, no lugar exato onde alguém vai diagnosticar
  // uma falha.
  const [fetchLogs, { currentData, isFetching, isError }] =
    useLazyGetDependencyLogsQuery();

  useEffect(() => {
    if (serviceKey) {
      setPage(1);
      fetchLogs({ serviceKey, page: 1, pageSize: PAGE_SIZE });
    }
  }, [serviceKey, fetchLogs]);

  const onChangePage = (nextPage: number) => {
    setPage(nextPage);
    if (serviceKey) {
      fetchLogs({ serviceKey, page: nextPage, pageSize: PAGE_SIZE });
    }
  };

  return (
    <Drawer
      title={`Últimas requisições · ${title}`}
      placement="right"
      width={520}
      visible={!!serviceKey}
      onClose={onClose}
    >
      <Spin spinning={isFetching}>
        {isError && (
          <Alert
            type="error"
            showIcon
            message="Não foi possível carregar as requisições deste serviço."
            description="Tente novamente; se persistir, verifique a permissão de acesso ao log de integração."
          />
        )}

        {!isError && currentData && currentData.results.length === 0 && (
          <Empty description="Nenhuma requisição registrada para este serviço." />
        )}

        {!isError &&
          currentData?.results.map((log) => <LogRow key={log.id} log={log} />)}

        {!isError &&
          currentData &&
          currentData.pagination.total_count > PAGE_SIZE && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Pagination
                simple
                current={page}
                pageSize={PAGE_SIZE}
                total={currentData.pagination.total_count}
                onChange={onChangePage}
              />
            </div>
          )}
      </Spin>
    </Drawer>
  );
}
